import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit3,
  LayoutDashboard,
  ListChecks,
  Lock,
  Plus,
  Save,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { budgetApi, projectApi } from "../../backend/client";
import type { BudgetSummary } from "../../backend/api/budgets";
import { useCurrentUser } from "../../backend/queries/auth";
import { invalidateDashboard } from "../../backend/queries/dashboard";
import { useProjectConversation } from "../../backend/queries/chat";
import {
  useAssignProjectParticipant,
  useEndProjectParticipant,
  useProjectParticipants,
} from "../../backend/queries/projects";
import { useAssignmentOptions } from "../../backend/queries/users";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  ConfirmDialog,
  ErrorState,
  LoadingState,
} from "../../components/patterns";
import { ConversationPane } from "../chat/ConversationPane";
import { MessageUserButton } from "../chat/MessageUserButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { formatMoney } from "../../components/MoneyInput";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import AgentExportPicker from "./AgentExportPicker";
import ProjectInvoices from "./ProjectInvoices";
import { exportAgentProjectPDF } from "./agentExportProjectPdf";
import PaymentForm from "./paymentform";
import Modal from "./projectmodal";
import { ProjectCustomerCard } from "./ProjectCustomerCard";
import AgentTasks from "../tasks/agentTasks";
import { useProjectMetadata } from "../../lib/useProjectMetadata";
import { cn } from "../../lib/utils";
import {
  labelForProjectOption,
  optionsIncludingCurrent,
  toneClassForProjectStatus,
} from "../../lib/projectMetadata";

const toDateInputValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};


const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: currentUser } = useCurrentUser();
  const currentRole = String(
    currentUser?.profile?.role || currentUser?.role || "",
  );
  const canManageCustomers = (currentRole === "Owner" || currentRole === "Admin");
  const canManageTeam = (currentRole === "Owner" || currentRole === "Admin");
  const isStaff = (currentRole === "Owner" || currentRole === "Admin") || currentRole === "Agent";
  const currentUserId = currentUser?.profile?.id || currentUser?.id;
  const participantsQuery = useProjectParticipants(
    String(projectId || ""),
    isStaff,
  );
  const projectConversationQuery = useProjectConversation(
    String(projectId || ""),
    { enabled: isStaff },
  );
  const projectConversation = projectConversationQuery.data;
  const teamParticipants = (participantsQuery.data || []).filter(
    (participant) =>
      participant.participant_role === "lead_agent" ||
      participant.participant_role === "supporting_agent",
  );
  const activeView =
    searchParams.get("view") === "tasks" ? "tasks" : "overview";
  const scrollToChat = searchParams.get("chat") === "1";

  const [project, setProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const {
    metadata,
    loading: metadataLoading,
    error: metadataError,
  } = useProjectMetadata();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const agentOptions = useAssignmentOptions(
    {
      kind: "agent",
      limit: 50,
    },
    // Only an Admin can change the project's staff member or team; Rust drops
    // `agent_id` from an Agent's update, so Agents never need the options.
    { enabled: canManageTeam },
  );
  const agents = agentOptions.data?.items || [];
  const assignParticipant = useAssignProjectParticipant();
  const endParticipant = useEndProjectParticipant();
  const [teamMemberId, setTeamMemberId] = useState("");
  const [teamError, setTeamError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [paymentDeleteError, setPaymentDeleteError] = useState("");
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [budgetError, setBudgetError] = useState("");
  const [loadError, setLoadError] = useState<{
    message: string;
    status?: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [exportBusy, setExportBusy] = useState(false);

  const fetchPayment = useCallback(async () => {
    const [paymentsResult, budgetResult] = await Promise.all([
      budgetApi.listPayments(projectId),
      budgetApi.getForProject(projectId),
    ]);

    // Shown on the page rather than only logged: an empty costs list or a
    // zero budget would otherwise read as real figures.
    setBudgetError(
      budgetResult.error
        ? budgetResult.error.message || "Budget figures could not be loaded."
        : "",
    );
    if (!budgetResult.error) {
      setBudgetSummary(budgetResult.data);
    }

    if (paymentsResult.error) {
      setPaymentsError(
        paymentsResult.error.message || "Costs could not be loaded.",
      );
      return;
    }
    setPaymentsError("");

    const sortedPayments = (paymentsResult.data || []).sort((left, right) => {
      const due = String(left.due_date || "").localeCompare(
        String(right.due_date || ""),
      );
      if (due !== 0) return due;
      return String(right.paid_on || "").localeCompare(
        String(left.paid_on || ""),
      );
    });
    setPayments(sortedPayments);
  }, [projectId]);

  const fetchProjectDetails = useCallback(async () => {
    const { data, error } = await projectApi.get(projectId);
    if (error) {
      setLoadError(error);
      return;
    }

    setLoadError(null);
    setProject(data);
    setEditedProject(data);
  }, [projectId]);

  useEffect(() => {
    fetchProjectDetails();
    fetchPayment();
  }, [fetchProjectDetails, fetchPayment]);

  // Waits for the conversation lookup to settle, because the card it scrolls
  // to only exists once that has happened.
  useEffect(() => {
    if (!scrollToChat || !isStaff || projectConversationQuery.isPending) {
      return;
    }
    document
      .getElementById("project-conversation")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollToChat, isStaff, projectConversationQuery.isPending]);

  const totalBudget = Number(
    budgetSummary?.total_budget ?? project?.total_budget ?? 0,
  );
  // Paid and outstanding come from Xero, through the budget summary. The portal
  // does not compute them from its own records: it has no authority to say an
  // invoice has been paid.
  const paidTotal = Number(budgetSummary?.amount_paid ?? 0);
  const outstandingTotal = Number(budgetSummary?.amount_pending ?? 0);
  const remainingBudget = Number(
    budgetSummary?.remaining_budget ?? totalBudget,
  );

  const clientName = project?.customer
    ? `${project.customer.firstname || ""} ${project.customer.lastname || ""}`.trim()
    : "N/A";

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditedProject((previous) => ({ ...previous, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setEditedProject((previous) => ({
      ...previous,
      [name]: value === "unassigned" ? null : value,
    }));
  };

  const handleDeleteProject = async () => {
    setDeleting(true);
    const { error } = await projectApi.delete(projectId);
    setDeleting(false);

    if (error) {
      setDeleteError(error.message || "Could not delete this project.");
      return;
    }

    await invalidateDashboard(queryClient);
    navigate("/projects");
  };

  const handleSave = async () => {
    const updatedFields = {
      status: editedProject.status,
      // Rust rejects a project type that is no longer offered, so resending an
      // unchanged legacy type would block every other edit to the project.
      ...(editedProject.project_type !== project.project_type
        ? { project_type: editedProject.project_type }
        : {}),
      project_start_date: editedProject.project_start_date,
      project_end_date: editedProject.project_end_date,
      project_notes: editedProject.project_notes,
      relocating_from: editedProject.relocating_from,
      relocating_to: editedProject.relocating_to,
      ...(canManageTeam ? { agent_id: editedProject.agent_id } : {}),
      total_budget: editedProject.total_budget,
    };

    setSaving(true);
    setSaveError("");
    const { error } = await projectApi.update(projectId, updatedFields);
    if (error) {
      setSaving(false);
      setSaveError(error.message || "Your changes could not be saved.");
      return;
    }

    await invalidateDashboard(queryClient);
    await Promise.all([fetchProjectDetails(), fetchPayment()]);
    setSaving(false);
    setIsEditing(false);
  };

  const handleCustomerUpdated = useCallback((customer) => {
    setProject((previous) => ({
      ...previous,
      customer: { ...previous?.customer, ...customer },
    }));
    setEditedProject((previous) => ({
      ...previous,
      customer: { ...previous?.customer, ...customer },
    }));
  }, []);

  const handleDelete = (paymentId) => {
    setPaymentDeleteError("");
    setPaymentToDelete(
      payments.find((payment) => payment.payment_id === paymentId) || {
        payment_id: paymentId,
      },
    );
  };

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    setDeletingPayment(true);
    setPaymentDeleteError("");
    const { error } = await budgetApi.deletePayment(paymentToDelete.payment_id);
    setDeletingPayment(false);
    if (error) {
      setPaymentDeleteError(error.message || "Could not delete this cost.");
      return;
    }
    setPaymentToDelete(null);
    fetchPayment();
  };

  const closeCostForm = () => {
    setEditingCost(null);
    setShowForm(false);
  };

  const handlePaymentSuccess = useCallback(async () => {
    await fetchPayment();
    setEditingCost(null);
    setShowForm(false);
  }, [fetchPayment]);

  const doExport = useCallback(async () => {
    try {
      setExportBusy(true);

      if (!payments || payments.length === 0) {
        await fetchPayment();
      }

      const projectName =
        (project?.project_type ? `${project.project_type} - ` : "") +
        `${project?.relocating_from || ""} -> ${project?.relocating_to || ""}`.trim();

      await exportAgentProjectPDF({
          projectId: String(project?.id ?? ""),
          projectName,
          project_type: project?.project_type,
          clientName,
          status: project?.status,
          createdAt:
            project?.project_start_date &&
            new Date(project.project_start_date).toLocaleDateString(),
          dueDate:
            project?.project_end_date &&
            new Date(project.project_end_date).toLocaleDateString(),
          budgetTotals: {
            planned: totalBudget,
            actual: paidTotal,
            remaining: remainingBudget,
          },
          payments: payments.map((payment) => ({
            item: payment.item,
            cost: Number(payment.cost ?? 0),
            due_date: payment.due_date,
            paid_on: payment.paid_on,
            payment_status: payment.payment_status,
            invoice: payment.invoice,
        })),
      });
    } finally {
      setExportBusy(false);
    }
  }, [
    clientName,
    fetchPayment,
    paidTotal,
    payments,
    project,
    remainingBudget,
    totalBudget,
  ]);

  if (!project || !editedProject) {
    const notFound = loadError?.status === 404;
    return (
      <div className="min-h-screen p-4 md:p-6">
        {loadError ? (
          <ErrorState
            size="page"
            title={
              notFound ? "Project not found" : "Project could not be loaded"
            }
            description={
              notFound
                ? "It may have been deleted, or you may no longer be assigned to it."
                : loadError.message
            }
            onRetry={notFound ? undefined : () => fetchProjectDetails()}
            actions={
              <Button asChild variant="ghost">
                <Link to="/projects">Back to projects</Link>
              </Button>
            }
          />
        ) : (
          <LoadingState size="page" label="Loading project details" />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen p-4 md:p-6">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
          {metadataError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {metadataError}
            </div>
          )}

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 space-y-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2 w-fit"
                onClick={() => navigate("/projects")}
              >
                <ArrowLeft className="h-4 w-4" />
                Projects
              </Button>
              <h1 className="break-words text-2xl font-semibold tracking-tight md:text-3xl">
                {project.relocating_from || "Project"} to{" "}
                {project.relocating_to || "destination"}
              </h1>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:justify-end">
              <Button asChild variant="outline"><Link to={`/properties?projectId=${projectId}`}>Properties & viewings</Link></Button>
              <Button asChild variant="outline"><Link to={`/project-updates?projectId=${projectId}`}>Project updates</Link></Button>
              <MessageUserButton
                userId={project.customer?.id}
                label="Message client"
              />
              <Button
                type="button"
                variant={activeView === "overview" ? "default" : "outline"}
                onClick={() => setSearchParams({}, { replace: true })}
              >
                <LayoutDashboard className="h-4 w-4" />
                Overview
              </Button>
              <Button
                type="button"
                variant={activeView === "tasks" ? "default" : "outline"}
                onClick={() => {
                  setIsEditing(false);
                  setSearchParams({ view: "tasks" }, { replace: true });
                }}
              >
                <ListChecks className="h-4 w-4" />
                Project tasks
              </Button>
              <Button
                type="button"
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  setDeleteError("");
                  setConfirmDelete(true);
                }}
              >
                Delete project
              </Button>
            </div>
          </div>

          {activeView === "tasks" ? (
            <AgentTasks projectId={projectId} />
          ) : (
            <>
              <div className="grid items-stretch gap-5 xl:grid-cols-[1.35fr_0.85fr]">
                <Card className="h-full">
                  <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                    <div>
                      <CardTitle>Project information</CardTitle>
                      <CardDescription>
                        Assignment, status, schedule, and relocation details.
                      </CardDescription>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={saving}
                            onClick={() => {
                              setEditedProject(project);
                              setSaveError("");
                              setIsEditing(false);
                            }}
                          >
                            <X className="h-4 w-4" />
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={saving}
                            onClick={handleSave}
                          >
                            <Save className="h-4 w-4" />
                            {saving ? "Saving..." : "Save"}
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                        >
                          <Edit3 className="h-4 w-4" />
                          Edit details
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {saveError && (
                      <p
                        role="alert"
                        className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      >
                        {saveError}
                      </p>
                    )}
                    <div className="grid gap-4 md:grid-cols-2">
                      <InfoField
                        label="Project Type"
                        value={
                          labelForProjectOption(
                            metadata.project_types,
                            project.project_type,
                          ) || "Not set"
                        }
                        isEditing={isEditing}
                        editComponent={
                          <Select
                            value={editedProject.project_type || "Private"}
                            disabled={metadataLoading || Boolean(metadataError)}
                            onValueChange={(value) =>
                              handleSelectChange("project_type", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select project type" />
                            </SelectTrigger>
                            <SelectContent>
                              {optionsIncludingCurrent(
                                metadata.project_types,
                                editedProject.project_type,
                              ).map((option) => (
                                <SelectItem
                                  key={option.code}
                                  value={option.code}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        }
                      />
                      <InfoField
                        label="Status"
                        toneClass={toneClassForProjectStatus(project.status)}
                        value={
                          labelForProjectOption(
                            metadata.statuses,
                            project.status,
                          ) || "Not set"
                        }
                        isEditing={isEditing}
                        editComponent={
                          <Select
                            value={editedProject.status || "Pending"}
                            disabled={metadataLoading || Boolean(metadataError)}
                            onValueChange={(value) =>
                              handleSelectChange("status", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              {optionsIncludingCurrent(
                                metadata.statuses,
                                editedProject.status,
                              ).map((option) => (
                                <SelectItem
                                  key={option.code}
                                  value={option.code}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        }
                      />
                      <InfoField
                        label="Staff"
                        value={
                          project.agent
                            ? [
                                `${project.agent.firstname || ""} ${project.agent.lastname || ""}`.trim(),
                                project.agent.email && `(${project.agent.email})`,
                              ]
                                .filter(Boolean)
                                .join(" ")
                            : "Unassigned"
                        }
                        isEditing={isEditing}
                        // Rust ignores `agent_id` on an Agent's update, so a
                        // picker would save nothing.
                        locked={!canManageTeam}
                        editComponent={
                          <Select
                            value={editedProject.agent_id || "unassigned"}
                            onValueChange={(value) =>
                              handleSelectChange("agent_id", value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select staff member" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">
                                Unassigned
                              </SelectItem>
                              {agentOptions.isPending && (
                                <div className="px-3 py-2 text-sm text-muted-foreground">
                                  Loading staff...
                                </div>
                              )}
                              {agentOptions.error && (
                                <div className="px-3 py-2 text-sm text-destructive">
                                  {agentOptions.error.message}
                                </div>
                              )}
                              {agents.map((agent) => (
                                <SelectItem
                                  key={agent.id}
                                  value={String(agent.id)}
                                >
                                  {`${agent.firstname || ""} ${agent.lastname || ""}`.trim()}{" "}
                                  - {agent.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        }
                      />
                      <InfoField
                        label="Total Budget"
                        value={formatMoney(totalBudget)}
                        isEditing={isEditing}
                        editComponent={
                          <Input
                            type="number"
                            name="total_budget"
                            value={editedProject.total_budget || ""}
                            onChange={handleInputChange}
                          />
                        }
                      />
                      <InfoField
                        label="Start Date"
                        value={formatDate(project.project_start_date)}
                        isEditing={isEditing}
                        editComponent={
                          <Input
                            type="date"
                            name="project_start_date"
                            value={toDateInputValue(
                              editedProject.project_start_date,
                            )}
                            onChange={handleInputChange}
                          />
                        }
                      />
                      <InfoField
                        label="End Date"
                        value={formatDate(project.project_end_date)}
                        isEditing={isEditing}
                        editComponent={
                          <Input
                            type="date"
                            name="project_end_date"
                            value={toDateInputValue(
                              editedProject.project_end_date,
                            )}
                            onChange={handleInputChange}
                          />
                        }
                      />
                      <InfoField
                        label="Relocating From"
                        value={project.relocating_from || "N/A"}
                        isEditing={isEditing}
                        editComponent={
                          <Input
                            name="relocating_from"
                            value={editedProject.relocating_from || ""}
                            onChange={handleInputChange}
                          />
                        }
                      />
                      <InfoField
                        label="Relocating To"
                        value={project.relocating_to || "N/A"}
                        isEditing={isEditing}
                        editComponent={
                          <Input
                            name="relocating_to"
                            value={editedProject.relocating_to || ""}
                            onChange={handleInputChange}
                          />
                        }
                      />
                      <InfoField
                        className="md:col-span-2"
                        label="Internal notes · staff only"
                        value={project.project_notes || "N/A"}
                        isEditing={isEditing}
                        editComponent={
                          <Textarea
                            name="project_notes"
                            value={editedProject.project_notes || ""}
                            onChange={handleInputChange}
                            rows={4}
                          />
                        }
                      />
                    </div>
                  </CardContent>
                </Card>

                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-1 xl:grid-rows-2">
                  <ProjectCustomerCard
                    customer={project.customer}
                    canEdit={canManageCustomers}
                    onCustomerUpdated={handleCustomerUpdated}
                  />

                  <Card className="h-full">
                    <CardHeader className="gap-3">
                      <div>
                        <CardTitle>Project Team</CardTitle>
                        <CardDescription>
                          Staff allocated to this project. Task owners are chosen from this team.
                        </CardDescription>
                      </div>
                      {canManageTeam && (
                        <div className="flex gap-2">
                          <Select value={teamMemberId} onValueChange={setTeamMemberId}>
                            <SelectTrigger className="min-w-0 flex-1">
                              <SelectValue placeholder="Choose staff member" />
                            </SelectTrigger>
                            <SelectContent>
                              {agents
                                .filter(
                                  (agent) =>
                                    !participantsQuery.data?.some(
                                      (participant) =>
                                        !participant.ends_at && participant.user_id === agent.id,
                                    ),
                                )
                                .map((agent) => (
                                  <SelectItem key={agent.id} value={agent.id}>
                                    {[agent.firstname, agent.lastname].filter(Boolean).join(" ")}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!teamMemberId || assignParticipant.isPending}
                            onClick={async () => {
                              setTeamError("");
                              try {
                                await assignParticipant.mutateAsync({
                                  projectId: String(projectId),
                                  userId: teamMemberId,
                                  participantRole: "supporting_agent",
                                });
                                setTeamMemberId("");
                              } catch (error) {
                                setTeamError(error instanceof Error ? error.message : "Could not add team member");
                              }
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {teamError && <p className="text-sm text-destructive">{teamError}</p>}
                      {participantsQuery.isPending && (
                        <p className="text-sm text-muted-foreground">
                          Loading assignments...
                        </p>
                      )}
                      {participantsQuery.error && (
                        <p className="text-sm text-destructive">
                          {participantsQuery.error.message}
                        </p>
                      )}
                      {teamParticipants.map((participant) => (
                        <div
                          key={participant.id}
                          className="flex items-start justify-between gap-3 rounded-md border p-3 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {[participant.user.firstname, participant.user.lastname]
                                .filter(Boolean)
                                .join(" ") || participant.user.email}
                            </p>
                            <p className="truncate text-muted-foreground">
                              {participant.participant_role === "lead_agent"
                                ? "Project lead"
                                : participant.participant_role === "supporting_agent"
                                  ? "Team member"
                                  : participant.participant_role.replaceAll("_", " ")} · {participant.user.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(participant.starts_at)} –{" "}
                              {participant.ends_at
                                ? formatDate(participant.ends_at)
                                : "Present"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={participant.ends_at ? "outline" : "default"}>
                              {participant.ends_at ? "Ended" : "Active"}
                            </Badge>
                            {canManageTeam &&
                              !participant.ends_at &&
                              participant.participant_role === "supporting_agent" && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Remove team member"
                                  disabled={endParticipant.isPending}
                                  onClick={async () => {
                                    setTeamError("");
                                    try {
                                      await endParticipant.mutateAsync({
                                        projectId: String(projectId),
                                        participantId: participant.id,
                                      });
                                    } catch (error) {
                                      setTeamError(error instanceof Error ? error.message : "Could not remove team member");
                                    }
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                          </div>
                        </div>
                      ))}
                      {!participantsQuery.isPending && teamParticipants.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No assignment history.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Budget Summary</CardTitle>
                      <CardDescription>
                        Budget against what Xero reports as invoiced and paid.
                      </CardDescription>
                    </CardHeader>
                    {budgetError ? (
                      // Zeros in place of figures that failed to load would
                      // read as "nothing paid", so show the failure instead.
                      <CardContent className="p-0">
                        <ErrorState
                          title="Budget figures could not be loaded"
                          description={budgetError}
                          onRetry={() => fetchPayment()}
                        />
                      </CardContent>
                    ) : (
                      <CardContent className="divide-y">
                        <SummaryRow
                          label="Total Budget"
                          value={formatMoney(totalBudget)}
                          amount={totalBudget}
                        />
                        <SummaryRow
                          label="Paid (Xero)"
                          value={formatMoney(paidTotal)}
                          amount={paidTotal}
                        />
                        <SummaryRow
                          label="Invoiced, unpaid (Xero)"
                          value={formatMoney(outstandingTotal)}
                          amount={outstandingTotal}
                        />
                        <SummaryRow
                          label="Remaining"
                          value={formatMoney(remainingBudget)}
                          amount={remainingBudget}
                        />
                      </CardContent>
                    )}
                  </Card>
                </div>
              </div>

              {isStaff && !projectConversationQuery.isPending ? (
                <Card id="project-conversation">
                  <CardHeader>
                    <CardTitle>Project conversation</CardTitle>
                    <CardDescription>
                      Message everyone assigned to this project.
                    </CardDescription>
                  </CardHeader>
                  {projectConversation ? (
                    <CardContent className="p-0">
                      <ConversationPane
                        conversationId={projectConversation.id}
                        currentUserId={currentUserId}
                        className="h-[28rem] border-t"
                      />
                    </CardContent>
                  ) : projectConversationQuery.isError ? (
                    // The hook turns a 404 into no conversation, so an error
                    // here is a real failure, not a missing participant.
                    <CardContent className="border-t p-0">
                      <ErrorState
                        title="The project conversation could not be loaded."
                        onRetry={() => projectConversationQuery.refetch()}
                      />
                    </CardContent>
                  ) : (
                    // Rendered rather than hidden: arriving here from the
                    // conversation button and finding nothing at all reads as
                    // a broken link, so say why instead.
                    <CardContent className="border-t pt-6 text-sm text-muted-foreground">
                      This project has no conversation, so there is nothing to
                      show here. Only people in a conversation can open it, so
                      if one exists you may not be a participant.
                    </CardContent>
                  )}
                </Card>
              ) : null}

              <Card>
                <CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between lg:space-y-0">
                  <div>
                    <CardTitle>Costs</CardTitle>
                    <CardDescription>
                      Costs on this project. Invoice them below; payment status comes from Xero.
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowForm(true);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Add cost
                    </Button>
                    <AgentExportPicker onExport={doExport} busy={exportBusy} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {paymentsError ? (
                    <ErrorState
                      title="Costs could not be loaded"
                      description={paymentsError}
                      onRetry={() => fetchPayment()}
                    />
                  ) : (
                    <PaymentSection
                      title="Project Costs"
                      emptyText="No costs recorded"
                      payments={payments}
                      editable
                      onEdit={(cost) => {
                        setEditingCost(cost);
                        setShowForm(true);
                      }}
                      onDelete={handleDelete}
                    />
                  )}
                </CardContent>
              </Card>

              <ProjectInvoices
                projectId={project?.id}
                clientName={clientName}
                onChanged={fetchPayment}
              />
            </>
          )}
        </div>
      </div>

      <Modal isOpen={showForm} onClose={closeCostForm}>
        <PaymentForm
          payment={editingCost}
          handleCloseForm={closeCostForm}
          projectId={project.id}
          onSuccess={handlePaymentSuccess}
        />
      </Modal>


      <ConfirmDialog
        open={Boolean(paymentToDelete)}
        onOpenChange={(open) => {
          if (!open) setPaymentToDelete(null);
        }}
        title="Delete this cost?"
        description={`${paymentToDelete?.item || "This cost"} is removed from the project. This cannot be undone.`}
        confirmLabel="Delete cost"
        tone="destructive"
        busy={deletingPayment}
        busyLabel="Deleting..."
        onConfirm={() => void confirmDeletePayment()}
      >
        {paymentDeleteError && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {paymentDeleteError}
          </p>
        )}
      </ConfirmDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              {project.relocating_from || "This project"} to{" "}
              {project.relocating_to || ""} and its tasks, comments, payments
              and documents are removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteError && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {deleteError}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteProject();
              }}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete project"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// View mode renders the value in a control-shaped box so the layout does not
// shift when edit mode swaps in real inputs. `toneClass` uses the frontend
// design system for semantic status presentation.
//
// A value that is not being edited sits on the muted fill with the quiet
// `--border`, keeping the `--input` outline for controls the user can change.
// The value text stays full strength so it remains readable. A `locked` field
// keeps that look in edit mode with a lock whose hint explains why.
const InfoField = ({
  className = "",
  label,
  value,
  isEditing,
  editComponent = null,
  toneClass = "",
  locked = false,
  lockedHint = "Only an admin can change this.",
}) => {
  const editable = isEditing && editComponent && !locked;
  const showLock = isEditing && locked;
  return (
    <div className={className}>
      <Label className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </Label>
      <div className="mt-2">
        {editable ? (
          editComponent
        ) : (
          <div
            className={cn(
              "flex min-h-10 w-full items-center gap-2 break-words rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground",
              toneClass && "font-medium",
              toneClass,
            )}
          >
            <span className="min-w-0 flex-1">{value}</span>
            {showLock && (
              <span title={lockedHint} className="shrink-0 text-muted-foreground">
                <Lock className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{lockedHint}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, amount }) => (
  <div className="flex min-h-14 items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
    <p className="text-xs font-medium uppercase text-muted-foreground">
      {label}
    </p>
    <p
      className={`text-base font-semibold tabular-nums ${
        // Overspend is the one number here that means something is wrong.
        Number(amount ?? 0) < 0 ? "text-tone-danger-foreground" : ""
      }`}
    >
      {value}
    </p>
  </div>
);

type PaymentId = string | number;

const costTypeLabel = (value?: string | null) =>
  value === "expense" ? "Expense" : "Charge";

/** Mirrors public.payment_net_amount so the screen agrees with the invoice. */
const netAmount = (payment: PaymentRecord) => {
  const line = Number(payment.quantity ?? 1) * Number(payment.cost || 0);
  const discount = Number(payment.discount || 0);
  const off =
    payment.discount_type === "percent" ? (line * discount) / 100 : discount;
  return Math.max(0, line - off);
};

const discountLabel = (payment: PaymentRecord) => {
  const discount = Number(payment.discount || 0);
  if (discount <= 0) return null;
  return payment.discount_type === "percent"
    ? `${discount}%`
    : formatMoney(discount);
};

const gstLabel = (payment: PaymentRecord) => {
  if (payment.gst_applies === false) return "GST free";
  return payment.gst_inclusive ? "Inclusive" : "Exclusive";
};

type PaymentRecord = {
  payment_id: PaymentId;
  item?: string | null;
  cost?: string | number | null;
  due_date?: string | null;
  paid_on?: string | null;
  payment_status?: string | null;
  cost_type?: string | null;
  quantity?: number | string | null;
  discount?: number | string | null;
  discount_type?: string | null;
  gst_applies?: boolean | null;
  invoice_id?: number | null;
  invoice?: string | null;
  gst_inclusive?: boolean | string | number | null;
};

type PaymentSectionProps = {
  title: string;
  emptyText: string;
  payments: PaymentRecord[];
  editable?: boolean;
  onEdit?: (payment: PaymentRecord) => void;
  onDelete?: (paymentId: PaymentId) => void | Promise<void>;
};

const PaymentSection = ({
  title,
  emptyText,
  payments,
  editable = false,
  onEdit = () => {},
  onDelete = () => {},
}: PaymentSectionProps) => (
  <section>
    <div className="space-y-3 md:hidden">
      {payments.length === 0 ? (
        <EmptyPayments text={emptyText} />
      ) : (
        payments.map((payment) => (
          <PaymentMobileCard
            key={payment.payment_id}
            payment={payment}
            editable={editable}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))
      )}
    </div>

    <div className="hidden rounded-md border md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="w-[80px] text-right">Qty</TableHead>
            <TableHead className="w-[130px] text-right">Unit cost</TableHead>
            <TableHead className="w-[120px] text-right">Discount</TableHead>
            <TableHead className="w-[130px] text-right">Total</TableHead>
            <TableHead className="w-[110px]">GST</TableHead>
            <TableHead className="w-[170px]">Type</TableHead>
            <TableHead className="w-[130px]">Invoiced</TableHead>
            {editable && (
              <TableHead className="w-[80px] text-right">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={editable ? 8 : 7}
                className="py-8 text-center text-sm text-muted-foreground"
              >
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            payments.map((payment) => (
              <PaymentTableRow
                key={payment.payment_id}
                payment={payment}
                editable={editable}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  </section>
);

const PaymentTableRow = ({
  payment,
  editable,
  onEdit,
  onDelete,
}: {
  payment: PaymentRecord;
  editable: boolean;
  onEdit: (payment: PaymentRecord) => void;
  onDelete: (paymentId: PaymentId) => void | Promise<void>;
}) => (
  <TableRow>
    <TableCell className="font-medium">{payment.item || "N/A"}</TableCell>
    <TableCell className="text-right">{Number(payment.quantity ?? 1)}</TableCell>
    <TableCell className="text-right">{formatMoney(payment.cost)}</TableCell>
    <TableCell className="text-right">{discountLabel(payment) || "-"}</TableCell>
    <TableCell className="text-right font-medium">
      {formatMoney(netAmount(payment))}
    </TableCell>
    <TableCell>{gstLabel(payment)}</TableCell>
    <TableCell>{costTypeLabel(payment.cost_type)}</TableCell>
    <TableCell>
      {payment.cost_type === "expense" ? (
        <span className="text-tone-danger-foreground">Not billed</span>
      ) : payment.invoice_id ? (
        <span className="text-tone-success-foreground">Invoiced</span>
      ) : (
        <span className="text-tone-warning-foreground">Not yet</span>
      )}
    </TableCell>
    {editable && (
      <TableCell>
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onEdit(payment)}
            aria-label="Edit cost"
            // An invoiced cost is a record of what was billed. Changing it here
            // would leave the portal and Xero disagreeing about the same money.
            disabled={Boolean(payment.invoice_id)}
            title={
              payment.invoice_id
                ? "Already invoiced. Delete the draft invoice to change this."
                : "Edit cost"
            }
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onDelete(payment.payment_id)}
            aria-label="Delete cost"
            disabled={Boolean(payment.invoice_id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    )}
  </TableRow>
);

const PaymentMobileCard = ({
  payment,
  editable,
  onEdit,
  onDelete,
}: {
  payment: PaymentRecord;
  editable: boolean;
  onEdit: (payment: PaymentRecord) => void;
  onDelete: (paymentId: PaymentId) => void | Promise<void>;
}) => (
  <div className="rounded-md border bg-card p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="break-words text-sm font-semibold">
          {payment.item || "Cost"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {formatMoney(netAmount(payment))}
          {" · "}
          {gstLabel(payment)}
          {" · "}
          {costTypeLabel(payment.cost_type)}
        </p>
      </div>
      <span
        className={`shrink-0 text-sm ${
          payment.cost_type === "expense"
            ? "text-tone-danger-foreground"
            : payment.invoice_id
              ? "text-tone-success-foreground"
              : "text-tone-warning-foreground"
        }`}
      >
        {payment.cost_type === "expense"
          ? "Not billed"
          : payment.invoice_id
            ? "Invoiced"
            : "Not yet"}
      </span>
    </div>

    {editable && (
      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onEdit(payment)}
          disabled={Boolean(payment.invoice_id)}
        >
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => onDelete(payment.payment_id)}
          aria-label="Delete cost"
          disabled={Boolean(payment.invoice_id)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    )}
  </div>
);

const EmptyPayments = ({ text }) => (
  <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
    {text}
  </div>
);

export default ProjectDetails;
